import React, { ReactNode } from 'react';
import {
  ConnectionProfile,
  FUB_PIPELINES_MAPPING_UPDATED, FubPipelineMapEntry,
  ProjectType,
  useFubPipelineMappingService
} from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';
import {
  AfterSubmitFunc,
  Button,
  ButtonSize,
  ButtonVariant,
  Dropdown,
  FormTemplate,
  Header4,
  LoadingAnimation,
  SimpleBlock, Text
} from 'ui-builder';
import { usePopupFormManager } from 'features/nekst-widgets';

function FubPipelineDropdown(
  props: {
    profile: ConnectionProfile,
  }
) {
  const fubPipelineService = useFubPipelineMappingService();

  const { data } = useDataLoaderNew(
    async () => fubPipelineService.getAllPipelines(props.profile.id)
  );

  if (data) {
    return (
      <Dropdown
        options={data.map((item) => ({
          value: item.id,
          label: item.name
        }))}
        source="pipelineId"
        label="Follow Up Boss Pipeline"
      />
    );
  } else {
    return (<LoadingAnimation />);
  }
}

function UpdatePipelineMappingForm(props: {
  profile: ConnectionProfile,
  projectType: ProjectType,
  initialPipelineId?: number,
  afterSubmitFunc?: AfterSubmitFunc
}) {
  const pipelineMappingService = useFubPipelineMappingService();

  return (
    <FormTemplate<{ pipelineId: number }>
      title="Choose Pipeline"
      submitFormFunc={async (data) => {
        await pipelineMappingService.updateMapEntry(
          props.profile.id,
          props.projectType,
          {
            pipelineId: data.pipelineId
          }
        );

        return data;
      }}
      initialData={{
        pipelineId: props.initialPipelineId
      }}
      afterSubmitFunc={props.afterSubmitFunc}
    >
      <FubPipelineDropdown profile={props.profile} />
    </FormTemplate>
  );
}

function NotLinkedView(props: {
  profile: ConnectionProfile,
  projectType: ProjectType,
}) {
  const popupManager = usePopupFormManager();

  return (
    <SimpleBlock>
      <p>
        This project is not linked to any Follow Up Boss pipeline.
      </p>
      <Button
        text="Choose Pipeline"
        variant={ButtonVariant.FILLED}
        size={ButtonSize.SMALL}
        onClick={() => {
          popupManager.openForm(
            <UpdatePipelineMappingForm
              profile={props.profile}
              projectType={props.projectType}
            />
          );
        }}
      />

    </SimpleBlock>
  );
}

function LinkedView(
  props: {
    profile: ConnectionProfile,
    projectType: ProjectType,
    mapEntry: FubPipelineMapEntry
  }
) {
  const popupManager = usePopupFormManager();

  return (
    <>
      <p>
        Linked to <Text bold>{props.mapEntry.fubPipeline.name}</Text> pipeline.
      </p>
      <SimpleBlock>
        <Button
          text="Update Link"
          onClick={async () => {
            popupManager.openForm((
              <UpdatePipelineMappingForm
                profile={props.profile}
                projectType={props.projectType}
                initialPipelineId={props.mapEntry.fubPipeline.id}
              />
            ))
          }}
          variant={ButtonVariant.FILLED}
          size={ButtonSize.SMALL}
        />
      </SimpleBlock>
    </>
  );
}

export function PipelineMapping(props: {
  profile: ConnectionProfile,
  projectType: ProjectType,
}) {
  const pipelineMappingService = useFubPipelineMappingService();

  const {
    data,
    loading
  } = useDataLoaderNew(
    async () => {
      return pipelineMappingService.getPipelineMapEntry(
        props.profile.id,
        props.projectType
      );
    },
    {
      dependencies: [props.profile.id, props.projectType],
      events: [FUB_PIPELINES_MAPPING_UPDATED]
    }
  );

  let content: ReactNode | null = null;

  if (loading) {
    content = (<LoadingAnimation />);
  } else {
    if (data) {
      content = (<LinkedView profile={props.profile} projectType={props.projectType} mapEntry={data} />);
    } else {
      content = (<NotLinkedView profile={props.profile} projectType={props.projectType} />);
    }
  }

  return (
    <>
      <Header4>Pipeline</Header4>
      {content}
    </>
  );
}
